import React, { useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { createEmployee } from "../../services/createEmployee";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import Loader from "../../utils/Loader";
import { usePermissions } from "../../context/PermissionsContext";
import { isAdminRole } from "../../helpers/employeeRole";
import RolesListBox from "./RolesListBox";

const ROLES = ["User", "Admin"];

function CreateEmployee() {
    const navigate = useNavigate();
    const { can, permissionsLoading } = usePermissions();
    const submitting = useRef(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        role: 0,
        createPasswordNow: false,
    });

    useEffect(() => {
        getCurrentEmployee();
    }, []);

    const getCurrentEmployee = async () => {
        const employee = await getLoggedInEmployee();
        if (employee.success) {
            setLoggedInEmployee(employee.data.employeeResponse);

        }
    };

    const onHandleChangeRole = (role) => {
        setForm({ ...form, role: role });
    };

    const onHandleChangePasswordOption = (createPasswordNow) => {
        setForm({
            ...form,
            createPasswordNow,
            password: createPasswordNow ? form.password : "",
        });
    };

    const authorized = !permissionsLoading && isAdminRole(loggedInEmployee?.role) && can("employees.write");

    async function create() {
        if (!authorized || submitting.current) return;

        if (
            form.first_name.trim() !== "" &&
            form.last_name.trim() !== "" &&
            form.email.trim() !== "" &&
            form.role !== "" &&
            (!form.createPasswordNow || form.password !== "")
        ) {
            submitting.current = true;
            setIsSubmitting(true);
            try {
                const employee = await createEmployee(form);

                if (employee.success && employee.data?.success !== false) {
                    toast.success(
                        employee.data.message ||
                            "Employee created. Check invitation delivery with your administrator.",
                    );
                    navigate("/employees");
                } else {
                    toast.error(
                        employee.data?.message ||
                            employee.message ||
                            "Something went wrong. Please contact us for assistance.",
                    );
                }
            } finally {
                submitting.current = false;
                setIsSubmitting(false);
            }
        } else {
            toast.error(
                form.createPasswordNow
                    ? "First name, Last name, Email, Role, and password can't be empty!"
                    : "First name, Last name, Email, and Role can't be empty!",
            );
        }
    }

    const disabledInputClass = isSubmitting
        ? " bg-slate-50 text-slate-500 cursor-not-allowed"
        : "";

    return (
        <section>
            <Toaster />
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        {!permissionsLoading && !authorized && <p role="alert" className="p-4 text-sm">An Admin with employees.write is required to create employees.</p>}
                        <fieldset disabled={!authorized || isSubmitting} className="min-w-0">
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-2xl">
                                <Breadcrumbs
                                    home="/employees"
                                    pages={[
                                        {
                                            name: "Create employee",
                                            href: `/employees/create`,
                                            current: true,
                                        },
                                    ]}
                                />

                                {/* Page header */}
                                <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                    {/* Left: Title */}
                                    <div className="mb-4 sm:mb-0">
                                        <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                            Create employee
                                        </h1>
                                    </div>
                                </div>

                                <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold text-slate-800">
                                            Details
                                        </h2>
                                    </header>

                                    <div className="px-5 py-4 space-y-5">
                                        <div>
                                            <label
                                                htmlFor="first_name"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                First Name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="first_name"
                                                    disabled={isSubmitting || !authorized}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            first_name:
                                                                e.target.value,
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.first_name}
                                                    className={`shadow-sm border border-slate-200 rounded-sm p-1 px-2 w-full max-w-sm focus:ring-0 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed${disabledInputClass}`}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="last_name"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Last Name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="last_name"
                                                    disabled={isSubmitting || !authorized}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            last_name:
                                                                e.target.value,
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.last_name}
                                                    className={`shadow-sm border border-slate-200 rounded-sm p-1 px-2 w-full max-w-sm focus:ring-0 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed${disabledInputClass}`}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Email
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="email"
                                                    disabled={isSubmitting || !authorized}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            email: e.target.value,
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.email}
                                                    className={`shadow-sm border border-slate-200 rounded-sm p-1 px-2 w-full max-w-sm focus:ring-0 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed${disabledInputClass}`}
                                                />
                                            </div>
                                        </div>
                                        {loggedInEmployee &&
                                            isAdminRole(loggedInEmployee.role) && (
                                                <div
                                                    className={`mt-1 ${
                                                        isSubmitting
                                                            ? "pointer-events-none opacity-60"
                                                            : ""
                                                    }`}
                                                >
                                                    <RolesListBox
                                                        roles={ROLES}
                                                        role={form.role}
                                                        onChange={(role) =>
                                                            onHandleChangeRole(role)
                                                        }
                                                    />
                                                </div>
                                            )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Invitation
                                            </label>
                                            <div className="max-w-sm mt-2 space-y-2">
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="createPasswordNow"
                                                        disabled={isSubmitting || !authorized}
                                                        checked={
                                                            !form.createPasswordNow
                                                        }
                                                        onChange={() =>
                                                            onHandleChangePasswordOption(
                                                                false,
                                                            )
                                                        }
                                                        className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">
                                                        Send invitation only
                                                    </span>
                                                </label>
                                                <label className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="createPasswordNow"
                                                        disabled={isSubmitting || !authorized}
                                                        checked={
                                                            form.createPasswordNow
                                                        }
                                                        onChange={() =>
                                                            onHandleChangePasswordOption(
                                                                true,
                                                            )
                                                        }
                                                        className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">
                                                        Create password now
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                        {form.createPasswordNow && (
                                            <div>
                                                <label
                                                    htmlFor="password"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Password
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        id="password"
                                                        disabled={isSubmitting || !authorized}
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                password:
                                                                    e.target.value,
                                                            })
                                                        }
                                                        type="password"
                                                        value={form.password}
                                                        className={`shadow-sm border border-slate-200 rounded-sm p-1 px-2 w-full max-w-sm focus:ring-0 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed${disabledInputClass}`}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end w-full">
                                    <button
                                        onClick={() => create()}
                                        type="submit"
                                        disabled={isSubmitting || !authorized}
                                        className="z-50 mt-4 ml-3 inline-flex min-w-[150px] items-center justify-center gap-2 py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-sm text-white bg-teal-500 hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting && <Loader />}
                                        {isSubmitting
                                            ? "Creating employee..."
                                            : "Create employee"}
                                    </button>
                                </div>
                            </div>
                        </div>
                        </fieldset>
                    </main>
                </div>
            </div>
        </section>
    );
}
export default CreateEmployee;
